import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { getLoans, getEquipment, getEmployees, createLoan, returnLoan, deleteLoan, getLoanPdf, uploadEquipmentImage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, RotateCcw, Download, Search, ImagePlus, X, Trash2 } from "lucide-react";

export default function Loans() {
  const { t } = useTranslation();
  const { canEdit, canDelete } = useAuth();
  const loanFileInputRef = useRef(null);
  const returnFileInputRef = useRef(null);
  const [loans, setLoans] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loanDialogOpen, setLoanDialogOpen] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [loanImages, setLoanImages] = useState([]);
  const [returnImages, setReturnImages] = useState([]);

  const [loanFormData, setLoanFormData] = useState({
    equipment_id: "",
    employee_id: "",
    expected_return_date: ""
  });

  const [returnFormData, setReturnFormData] = useState({
    condition_at_return: t('conditions.good'),
    return_notes: ""
  });

  const CONDITIONS = [
    t('conditions.new'),
    t('conditions.veryGood'),
    t('conditions.good'),
    t('conditions.acceptable'),
    t('conditions.defective')
  ];

  const handleLoanFileSelect = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      if (!file.type.startsWith("image/")) return;
      if (file.size > 5 * 1024 * 1024) return;
      const reader = new FileReader();
      reader.onload = (event) => {
        setLoanImages(prev => [...prev, {
          id: Date.now() + Math.random(),
          data: event.target.result,
          description: t('loans.documentCondition')
        }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const handleReturnFileSelect = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      if (!file.type.startsWith("image/")) return;
      if (file.size > 5 * 1024 * 1024) return;
      const reader = new FileReader();
      reader.onload = (event) => {
        setReturnImages(prev => [...prev, {
          id: Date.now() + Math.random(),
          data: event.target.result,
          description: t('loans.documentReturnCondition')
        }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const removeLoanImage = (id) => setLoanImages(prev => prev.filter(img => img.id !== id));
  const removeReturnImage = (id) => setReturnImages(prev => prev.filter(img => img.id !== id));
  
  const updateLoanImageDesc = (id, desc) => {
    setLoanImages(prev => prev.map(img => img.id === id ? {...img, description: desc} : img));
  };
  const updateReturnImageDesc = (id, desc) => {
    setReturnImages(prev => prev.map(img => img.id === id ? {...img, description: desc} : img));
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [loansRes, equipmentRes, employeesRes] = await Promise.all([
        getLoans(),
        getEquipment(),
        getEmployees()
      ]);
      setLoans(loansRes.data);
      setEquipment(equipmentRes.data.filter(e => e.status === "Verfügbar"));
      setEmployees(employeesRes.data);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLoan = async (e) => {
    e.preventDefault();
    try {
      await createLoan(loanFormData);
      
      if (loanImages.length > 0 && loanFormData.equipment_id) {
        for (const img of loanImages) {
          await uploadEquipmentImage(loanFormData.equipment_id, {
            image_data: img.data,
            description: img.description
          });
        }
      }
      
      toast.success(t('loans.success'));
      setLoanDialogOpen(false);
      setLoanFormData({ equipment_id: "", employee_id: "", expected_return_date: "" });
      setLoanImages([]);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const handleReturn = async (e) => {
    e.preventDefault();
    if (!selectedLoan) return;
    try {
      await returnLoan(selectedLoan.id, returnFormData);
      
      if (returnImages.length > 0 && selectedLoan.equipment_id) {
        for (const img of returnImages) {
          await uploadEquipmentImage(selectedLoan.equipment_id, {
            image_data: img.data,
            description: img.description
          });
        }
      }
      
      toast.success(t('loans.returnSuccess'));
      setReturnDialogOpen(false);
      setSelectedLoan(null);
      setReturnFormData({ condition_at_return: t('conditions.good'), return_notes: "" });
      setReturnImages([]);
      loadData();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const openReturnDialog = (loan) => {
    setSelectedLoan(loan);
    setReturnFormData({ condition_at_return: loan.condition_at_loan, return_notes: "" });
    setReturnDialogOpen(true);
  };

  const handleDownloadPdf = (loanId) => {
    const token = localStorage.getItem("token");
    const pdfUrl = getLoanPdf(loanId);
    
    fetch(pdfUrl, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `uebergabe_${loanId.substring(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => toast.error(t('common.error')));
  };

  const handleDeleteLoan = async (loan) => {
    if (!window.confirm(`Möchten Sie die Ausleihe von "${loan.equipment_name}" an ${loan.employee_name} wirklich löschen?`)) return;
    try {
      await deleteLoan(loan.id);
      toast.success("Ausleihe gelöscht");
      loadData();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };


  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("de-DE");
    } catch {
      return dateString;
    }
  };

  const filteredLoans = loans.filter((loan) => {
    const matchesSearch = 
      loan.equipment_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      loan.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      loan.equipment_serial?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "all" || loan.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  return (
    <div data-testid="loans-page">
      <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">{t('loans.title')}</h1>
          <p className="page-subtitle">{t('loans.subtitle')}</p>
        </div>
        {canEdit() && (
          <Dialog open={loanDialogOpen} onOpenChange={setLoanDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary gap-2" data-testid="new-loan-btn">
                <Plus className="w-4 h-4" />
                {t('loans.new')}
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-lg" data-testid="loan-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t('loans.loanEquipment')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateLoan} className="space-y-4 mt-4">
              <div>
                <Label className="form-label">{t('loans.equipment')}</Label>
                <Select
                  value={loanFormData.equipment_id}
                  onValueChange={(value) => setLoanFormData({ ...loanFormData, equipment_id: value })}
                  required
                >
                  <SelectTrigger data-testid="loan-equipment-select">
                    <SelectValue placeholder={t('loans.selectEquipment')} />
                  </SelectTrigger>
                  <SelectContent>
                    {equipment.length === 0 ? (
                      <SelectItem value="none" disabled>
                        {t('loans.noEquipmentAvailable')}
                      </SelectItem>
                    ) : (
                      equipment.map((eq) => (
                        <SelectItem key={eq.id} value={eq.id}>
                          {eq.name} ({eq.serial_number})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">{t('loans.employee')}</Label>
                <Select
                  value={loanFormData.employee_id}
                  onValueChange={(value) => setLoanFormData({ ...loanFormData, employee_id: value })}
                  required
                >
                  <SelectTrigger data-testid="loan-employee-select">
                    <SelectValue placeholder={t('loans.selectEmployee')} />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name} ({emp.employee_number})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">{t('loans.expectedReturn')}</Label>
                <Input
                  type="date"
                  value={loanFormData.expected_return_date}
                  onChange={(e) => setLoanFormData({ ...loanFormData, expected_return_date: e.target.value })}
                  data-testid="loan-return-date-input"
                />
              </div>
              
              <div className="border-t pt-4 mt-2">
                <div className="flex items-center justify-between mb-3">
                  <Label className="form-label mb-0">{t('loans.photos')}</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => loanFileInputRef.current?.click()}
                    className="gap-2"
                  >
                    <ImagePlus className="w-4 h-4" />
                    {t('common.photo')}
                  </Button>
                  <input
                    ref={loanFileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    capture="environment"
                    onChange={handleLoanFileSelect}
                    className="hidden"
                  />
                </div>
                {loanImages.length > 0 ? (
                  <div className="space-y-2">
                    {loanImages.map((img) => (
                      <div key={img.id} className="flex gap-2 p-2 bg-slate-50 rounded-lg border">
                        <img src={img.data} alt="Vorschau" className="w-16 h-16 object-cover rounded" />
                        <div className="flex-1">
                          <Input
                            value={img.description}
                            onChange={(e) => updateLoanImageDesc(img.id, e.target.value)}
                            placeholder={t('common.description')}
                            className="text-sm h-8"
                          />
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeLoanImage(img.id)} className="text-red-600 text-xs mt-1 h-6 px-2">
                            <X className="w-3 h-3 mr-1" />{t('equipment.remove')}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-3 bg-slate-50 rounded-lg border border-dashed">
                    {t('loans.documentCondition')}
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setLoanDialogOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" className="btn-primary" data-testid="loan-submit-btn">
                  {t('loans.loanEquipment')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Return Dialog */}
      <Dialog open={returnDialogOpen} onOpenChange={setReturnDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="return-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('loans.returnTitle')}
            </DialogTitle>
          </DialogHeader>
          {selectedLoan && (
            <div className="mt-4 p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">{t('loans.equipment')}:</p>
              <p className="font-medium">{selectedLoan.equipment_name}</p>
              <p className="text-sm text-slate-500">{selectedLoan.equipment_serial}</p>
            </div>
          )}
          <form onSubmit={handleReturn} className="space-y-4 mt-4">
            <div>
              <Label className="form-label">{t('loans.conditionAtReturn')}</Label>
              <Select
                value={returnFormData.condition_at_return}
                onValueChange={(value) => setReturnFormData({ ...returnFormData, condition_at_return: value })}
              >
                <SelectTrigger data-testid="return-condition-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONDITIONS.map((cond) => (
                    <SelectItem key={cond} value={cond}>
                      {cond}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="form-label">{t('loans.returnNotes')}</Label>
              <Textarea
                value={returnFormData.return_notes}
                onChange={(e) => setReturnFormData({ ...returnFormData, return_notes: e.target.value })}
                placeholder={t('loans.returnNotesPlaceholder')}
                rows={3}
                data-testid="return-notes-input"
              />
            </div>
            
            <div className="border-t pt-4 mt-2">
              <div className="flex items-center justify-between mb-3">
                <Label className="form-label mb-0">{t('loans.photosReturn')}</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => returnFileInputRef.current?.click()}
                  className="gap-2"
                >
                  <ImagePlus className="w-4 h-4" />
                  {t('common.photo')}
                </Button>
                <input
                  ref={returnFileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  capture="environment"
                  onChange={handleReturnFileSelect}
                  className="hidden"
                />
              </div>
              {returnImages.length > 0 ? (
                <div className="space-y-2">
                  {returnImages.map((img) => (
                    <div key={img.id} className="flex gap-2 p-2 bg-slate-50 rounded-lg border">
                      <img src={img.data} alt="Vorschau" className="w-16 h-16 object-cover rounded" />
                      <div className="flex-1">
                        <Input
                          value={img.description}
                          onChange={(e) => updateReturnImageDesc(img.id, e.target.value)}
                          placeholder={t('common.description')}
                          className="text-sm h-8"
                        />
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeReturnImage(img.id)} className="text-red-600 text-xs mt-1 h-6 px-2">
                          <X className="w-3 h-3 mr-1" />{t('equipment.remove')}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-3 bg-slate-50 rounded-lg border border-dashed">
                  {t('loans.documentReturnCondition')}
                </p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setReturnDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" className="btn-primary" data-testid="return-submit-btn">
                {t('loans.confirmReturn')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder={t('loans.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="loan-search"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full sm:w-48" data-testid="loan-filter-status">
            <SelectValue placeholder={t('loans.status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('equipment.allStatus')}</SelectItem>
            <SelectItem value="Ausgeliehen">{t('loans.statusLoaned')}</SelectItem>
            <SelectItem value="Zurückgegeben">{t('loans.statusReturned')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/50">
              <TableHead className="font-semibold">{t('loans.equipment')}</TableHead>
              <TableHead className="font-semibold">{t('equipment.serialNumber')}</TableHead>
              <TableHead className="font-semibold">{t('loans.employee')}</TableHead>
              <TableHead className="font-semibold">{t('loans.loanDate')}</TableHead>
              <TableHead className="font-semibold">{t('loans.returnDate')}</TableHead>
              <TableHead className="font-semibold">{t('loans.status')}</TableHead>
              <TableHead className="w-32">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : filteredLoans.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                  {t('loans.noEntries')}
                </TableCell>
              </TableRow>
            ) : (
              filteredLoans.map((loan) => (
                <TableRow key={loan.id} className="table-row-hover" data-testid={`loan-row-${loan.id}`}>
                  <TableCell className="font-medium">{loan.equipment_name}</TableCell>
                  <TableCell className="font-mono text-sm text-slate-600">
                    {loan.equipment_serial}
                  </TableCell>
                  <TableCell>{loan.employee_name}</TableCell>
                  <TableCell>{formatDate(loan.loan_date)}</TableCell>
                  <TableCell>
                    {loan.actual_return_date 
                      ? formatDate(loan.actual_return_date)
                      : loan.expected_return_date 
                        ? `${t('loans.planned')}: ${formatDate(loan.expected_return_date)}`
                        : "-"
                    }
                  </TableCell>
                  <TableCell>
                    {loan.status === "Ausgeliehen" ? (
                      <span className="badge-warning">{t('loans.statusLoaned')}</span>
                    ) : (
                      <span className="badge-success">{t('loans.statusReturned')}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {loan.status === "Ausgeliehen" && canEdit() && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openReturnDialog(loan)}
                          className="gap-1"
                          data-testid={`return-btn-${loan.id}`}
                        >
                          <RotateCcw className="w-3 h-3" />
                          {t('loans.return')}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDownloadPdf(loan.id)}
                        className="h-8 w-8"
                        title="Übergabedokument herunterladen"
                        data-testid={`pdf-btn-${loan.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      {canDelete() && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteLoan(loan)}
                          className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                          title="Ausleihe löschen"
                          data-testid={`delete-loan-btn-${loan.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
